import { HttpErrorResponse } from '@angular/common/http';

/**
 * A client-side request timeout, raised by `HttpTimeoutInterceptor` when a request exceeds its
 * configured budget.
 *
 * It extends `HttpErrorResponse` deliberately. Every existing consumer — `ErrorInterceptor`,
 * `AppInsightsInterceptor`, and the feature effects — narrows on `HttpErrorResponse` and reads
 * `.status` / `.message`, so letting a bare RxJS `TimeoutError` escape would surface as
 * `status: undefined`: telemetry would compute a `NaNxx` status class and the global error state
 * would receive the raw string "Timeout has occurred".
 *
 * `status` is 0 because no response was received, which also yields the correct App Insights
 * severity via `getSeverityByStatusCode`. `isTimeout` is what separates this from a genuine
 * network-down status 0 — classify on the flag, never on the status alone.
 */
export class HttpTimeoutError extends HttpErrorResponse {
  readonly isTimeout = true;
  readonly timeoutMs: number;

  constructor(url: string, timeoutMs: number) {
    super({
      url,
      status: 0,
      statusText: 'Client Timeout',
      error: new Error(`Request exceeded the ${timeoutMs}ms client timeout.`),
    });

    this.timeoutMs = timeoutMs;
  }
}

export function isHttpTimeoutError(error: unknown): error is HttpTimeoutError {
  return error instanceof HttpTimeoutError;
}
